import { darkTheme, lightTheme } from '@controlrooms/design-tokens';

// Helper function to convert hex to rgba
const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const modelVisualizerTheme = Object.fromEntries(
  Object.entries({ light: lightTheme, dark: darkTheme }).map(([key, theme]) => {
    const highlightColor = theme.color.lighting['highlight-1'];
    const shadowColor = theme.color.lighting['shadow-1'];
    const highlightRgba = hexToRgba(highlightColor, 0.1);
    const shadowRgba = hexToRgba(shadowColor, 0.1);

    return [
      key,
      {
        xAxisBG: theme.color.surface[2],
        textPrimary: theme.color.text.core['-primary'],
        textSecondary: theme.color.text.core['-secondary'],
        accordion: {
          headerBG: theme.color.dataVis.core['surface-1'],
          headerText: theme.color.text.core['-primary'],
          headerBorder: theme.color.surface[4],
        },
        trendChart: {
          background: theme.color.dataVis.core['surface-1'],
          chartTitle: theme.color.text.core['-primary'],
        },
        areaBG: `${theme.color.dataVis.core['data-tertiary-halftone']}50`,
        criticalArea1BG: `${theme.color.dataVis.core['critical-1-halftone']}45`,
        criticalArea2BG: `${theme.color.dataVis.core['critical-2-halftone']}50`,
        thresholdLineColor: '#AC6A05',
        lookbackLineColor: '#5BA0CB', // Light blue color from Figma
        lookbackLineSegment2Color: '#0D5C8E', // Darker blue for second segment
        dataLine: theme.color.dataVis.core['data-emphasis'],
        dataLineCritical1: theme.color.dataVis.alert['limit-2-halftone'],
        dataLineCritical2: theme.color.dataVis.alert['limit-2-halftone'],
        axisTick: theme.color.dataVis.core['data-tertiary'],
        axisGridLine: theme.color.dataVis.core['data-tertiary'],
        predictedAreaBG: theme.color.dataVis.qualitative['blue-1'],
        predictedAreaBorder: theme.color.dataVis.qualitative['blue-2'],
        rollingAverageLine: theme.color.dataVis.qualitative['blue-2'],
        rollingAverageMeanLine: theme.color.dataVis.qualitative['moss-2'],
        rollingAverageMADLine: theme.color.dataVis.qualitative['magenta-1'],
        predictedLine: theme.color.dataVis.core['data-emphasis'],
        anomalyLine: theme.color.dataVis.alert['anomaly-3'],
        crosshairLine: theme.color.focus[3],
        dataCircle: theme.color.focus[1],
        exclusionAreaBG: theme.color.dataVis.core['surface-1-halftone-75%'],
        tooltipBG: theme.color.surface[0],
        tooltipText: theme.color.text.core['-primary'],
        brushArea: theme.color.focus[3],
        tagNameBG: theme.color.dataVis.core['surface-1-halftone-75%'],
        tagNameText: theme.color.text.core['-primary'],
        inputDisabledBG: theme.color.surface[2],
        scaleLimitsPopup: {
          background: theme.color.surface[1],
          border: theme.color.surface[3],
          headerText: theme.color.text.core['-tertiary'],
          inputBackground: theme.color.control.button.secondary,
          inputBorder: theme.color.surface[3],
          inputText: theme.color.text.core['-primary'],
          focusBorder: theme.color.focus[3],
        },
        controlPill: {
          background: theme.color.surface[0],
          primaryBackground: theme.color.surface[0],
          secondaryBackground: theme.color.surface[3],
          boxShadow: `0 1px 1px 0 ${highlightRgba} inset, 0 -4px 4px 0 ${highlightRgba}, 0 -2px 4px 0 ${shadowRgba} inset, 0 4px 6px 0 ${shadowRgba}`,
          toggleBackground: theme.color.surface[4],
          toggleBorder: theme.color.surface[3],
          toggleSelectedBackground: theme.color.surface[1],
        },
        buttonHighlight: {
          textColor: theme.color.focus[1],
          backgroundColor: `${theme.color.focus[3]}20`,
        },
      },
    ];
  }),
);
